import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { useToastQueue, type Toast, type ToastVariant } from '@/hooks/useToast'
import { cn } from '@/lib/cn'

const VARIANT: Record<ToastVariant, {
  icon: typeof CheckCircle2
  iconClass: string
  border: string
  title: string
}> = {
  success: { icon: CheckCircle2,  iconClass: 'text-success-content', border: 'border-l-success', title: 'Success' },
  error:   { icon: XCircle,       iconClass: 'text-danger-content',    border: 'border-l-danger',    title: 'Error' },
  info:    { icon: Info,          iconClass: 'text-info-content',     border: 'border-l-info',     title: 'Info' },
  warning: { icon: AlertTriangle, iconClass: 'text-warning-content',   border: 'border-l-warning',   title: 'Warning' },
}

function ToastCard({ toast, message, onDismiss }: { toast: Toast; message: string; onDismiss: (id: number) => void }) {
  const v = VARIANT[toast.variant]
  const Icon = v.icon
  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
      className={cn(
        'pointer-events-auto relative flex w-80 max-w-[calc(100vw-2rem)] items-start gap-3 rounded-lg border border-border border-l-4 bg-card p-3 shadow-card-hover',
        'animate-slide-in-right',
        v.border,
      )}
    >
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', v.iconClass)} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-foreground">{toast.title ?? v.title}</div>
        <div className="mt-0.5 break-words text-xs text-content-secondary">{message}</div>
        {toast.action && (
          <button
            type="button"
            onClick={() => { toast.action!.onClick(); onDismiss(toast.id) }}
            className="mt-1.5 text-xs font-semibold text-link underline-offset-2 hover:underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="rounded p-1 text-content-placeholder hover:bg-muted hover:text-content-secondary"
        aria-label="Dismiss notification"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

const NEWLINE = String.fromCharCode(10)

export function Toaster() {
  const { toasts, dismiss } = useToastQueue()
  return (
    // Top-right, directly under the 3.5rem topbar (plus the notch inset), so a toast never hides
    // the page's own primary actions at the bottom. The stack is click-through (pointer-events-none);
    // only each card -- and so its Dismiss / action button -- takes clicks.
    <div className="pointer-events-none fixed right-4 top-[calc(3.5rem+env(safe-area-inset-top)+0.5rem)] z-[100] flex flex-col items-end gap-2 print:hidden">
      {toasts.flatMap((t) => {
        // One toast per validation error: a multi-line message (one line per field error) is split
        // into its own card each; dismissing any of them clears the whole group.
        const lines = t.message.split(NEWLINE).map((l) => l.trim()).filter(Boolean)
        return (lines.length > 1 ? lines : [t.message]).map((line, i) => (
          <ToastCard key={`${t.id}-${i}`} toast={t} message={line} onDismiss={dismiss} />
        ))
      })}
    </div>
  )
}
